import type { KindTotals } from "@cmmd-forge/shared/usageMerge";

import { shouldShowKindBreakdown } from "./usageKindRows";

/** Which split of the total the breakdown section is drawing. */
export type UsageBreakdownMode = "environment" | "provider" | "model" | "time" | "driver" | "kind";

const BREAKDOWN_MODES: readonly UsageBreakdownMode[] = [
  "environment",
  "provider",
  "model",
  "time",
  "driver",
  "kind",
];

export function isBreakdownMode(value: string | undefined): value is UsageBreakdownMode {
  return value !== undefined && (BREAKDOWN_MODES as readonly string[]).includes(value);
}

/** One toggle in the breakdown group. */
export interface UsageBreakdownOption {
  readonly value: UsageBreakdownMode;
  readonly label: string;
}

/** What the toggle offers, and the mode the section actually draws. */
export interface UsageBreakdownChoices {
  readonly mode: UsageBreakdownMode;
  readonly options: readonly UsageBreakdownOption[];
}

/**
 * The modes worth offering, and the one to draw.
 *
 * Kind is offered only when subagent or text generation work holds something:
 * until the emit cards land every row is a turn, and a split whose other two
 * rows are zero says nothing. The mode is state on the tab and outlives the
 * refresh that emptied the split, so a Kind mode with nothing to show falls
 * back to Model rather than drawing a table the toggle no longer offers.
 */
export function usageBreakdownChoices(
  mode: UsageBreakdownMode,
  kinds: readonly KindTotals[],
  resolution: "day" | "hour",
): UsageBreakdownChoices {
  const showKinds = shouldShowKindBreakdown(kinds);
  return {
    mode: mode === "kind" && !showKinds ? "model" : mode,
    options: [
      { value: "environment", label: "Environment" },
      { value: "provider", label: "Provider" },
      { value: "model", label: "Model" },
      { value: "time", label: resolution === "hour" ? "Hour" : "Day" },
      { value: "driver", label: "Driver" },
      ...(showKinds ? [{ value: "kind" as const, label: "Kind" }] : []),
    ],
  };
}
